/**
 * Pure header-mapping + preview logic for the client CSV import (WS-14), factored out of the modal so it
 * is unit-testable without React. It maps a parsed CSV (lib/csvParse → string[][]) onto the SAFE import
 * row subset and flags obvious problems CLIENT-SIDE for the preview — but those flags are advisory only;
 * the SERVER is the sole authority on what is invalid/duplicate, and the modal surfaces the server's
 * counts verbatim (never a client guess).
 */
import type { ImportClientRow } from '@salonomy/types';

/** Field a header column maps to, or null if the column is ignored. */
type Field = 'firstName' | 'lastName' | 'email' | 'phone' | 'notes';

/**
 * Case-insensitive header aliases (the locked design). A single "name"/"full name" column maps to
 * firstName so a one-column list still imports. Order matters only for diagnostics; each header cell is
 * resolved independently.
 */
const ALIASES: Record<Field, readonly string[]> = {
  firstName: ['first name', 'firstname', 'first', 'name', 'full name', 'fullname', 'client'],
  lastName: ['last name', 'lastname', 'surname', 'family name'],
  email: ['email', 'e-mail', 'email address'],
  phone: ['phone', 'mobile', 'telephone', 'tel', 'phone number', 'cell'],
  notes: ['notes', 'note', 'comments', 'comment'],
};

function normaliseHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Resolve one header cell to a field (first matching alias wins), or null if unrecognised. */
export function headerToField(header: string): Field | null {
  const n = normaliseHeader(header);
  for (const field of Object.keys(ALIASES) as Field[]) {
    if (ALIASES[field].includes(n)) return field;
  }
  return null;
}

/**
 * Build a column-index → field map from the header row. If two columns map to the same field the FIRST
 * wins (a later "name" column won't clobber an explicit "first name"). lastName never overrides a
 * firstName already taken from a single-name column.
 */
export function mapHeaders(headerRow: readonly string[]): (Field | null)[] {
  const used = new Set<Field>();
  return headerRow.map((cell) => {
    const field = headerToField(cell);
    if (field && !used.has(field)) {
      used.add(field);
      return field;
    }
    return null;
  });
}

export interface MappedRow {
  /** Strict-shaped row to POST. null/empty optional fields are OMITTED, not sent as "". */
  row: ImportClientRow;
  /** Advisory client-side flag: firstName is blank (the server will reject it as invalid). */
  missingFirstName: boolean;
  /** Advisory client-side flag: an email is present but doesn't look like an email. */
  badEmail: boolean;
}

export interface ParsedImport {
  /** Whether a recognisable header row was detected. */
  hasHeader: boolean;
  /** The resolved column→field mapping (for diagnostics / preview). */
  columns: (Field | null)[];
  /** All data rows, mapped + flagged. */
  rows: MappedRow[];
}

// Advisory only — deliberately loose. The server's Email primitive (z.email) is the real gate.
const EMAIL_LOOKS_OK = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function clean(v: string | undefined): string {
  return (v ?? '').trim();
}

function buildRow(get: (f: Field) => string): MappedRow {
  const firstName = get('firstName');
  const lastName = get('lastName');
  const email = get('email');
  const phone = get('phone');
  const notes = get('notes');

  const row: ImportClientRow = { firstName };
  if (lastName) row.lastName = lastName;
  if (email) row.email = email;
  if (phone) row.phone = phone;
  if (notes) row.notes = notes;

  return {
    row,
    missingFirstName: firstName.length === 0,
    badEmail: email.length > 0 && !EMAIL_LOOKS_OK.test(email),
  };
}

/**
 * Map a parsed CSV (the raw string[][] from parseCsv) into import rows. The first non-empty row is
 * treated as the header IF at least one cell maps to a known field; otherwise the file is assumed
 * header-less and a single column is used as firstName. Fully-blank rows are dropped.
 */
export function mapCsv(grid: readonly (readonly string[])[]): ParsedImport {
  // Drop fully-empty rows (e.g. a stray blank line) up front.
  const nonEmpty = grid.filter((r) => r.some((c) => c.trim().length > 0));
  if (nonEmpty.length === 0) return { hasHeader: false, columns: [], rows: [] };

  const first = nonEmpty[0]!;
  const headerCols = mapHeaders(first);
  const hasHeader = headerCols.some((c) => c !== null);

  if (hasHeader) {
    const dataRows = nonEmpty.slice(1);
    const rows = dataRows.map((r) => {
      const get = (f: Field): string => {
        const idx = headerCols.indexOf(f);
        return idx >= 0 ? clean(r[idx]) : '';
      };
      return buildRow(get);
    });
    return { hasHeader: true, columns: headerCols, rows };
  }

  // No header: treat the single first column as firstName (a bare list of names).
  const columns: (Field | null)[] = first.map((_, i) => (i === 0 ? 'firstName' : null));
  const rows = nonEmpty.map((r) => buildRow((f) => (f === 'firstName' ? clean(r[0]) : '')));
  return { hasHeader: false, columns, rows };
}
